import React, { useContext, useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { AuthContext } from '../../context/AuthContext';
import { useReservationShare } from '../../hooks/useReservationShare';
import { useUserProfile } from '../../hooks/useUserProfile';
import { reservationService } from '../../services/reservationService';
import Layout from '../../components/Layout/Layout';
import Button from '../../components/Common/Button';
import ReservationPDF from '../../components/Reservation/ReservationPDF';
import SEO from '../../components/SEO/SEO';

import {
    FaArrowLeft,
    FaFileDownload,
    FaWhatsapp,
    FaSpinner,
    FaExclamationCircle,
    FaCheckCircle,
    FaClock,
    FaCalendarAlt,
    FaDollarSign,
    FaFileAlt,
    FaInfoCircle
} from 'react-icons/fa';
import styles from './styles/ReservationDetailPage.module.css';

export default function ReservationDetailPage() {
    const { id } = useParams();
    const navigate = useNavigate();
    const { user } = useContext(AuthContext);
    const { userProfile } = useUserProfile();
    const { sendViaWhatsApp, downloadPDF, loading: sharingLoading } = useReservationShare();

    const [reservation, setReservation] = useState(null);
    const [loadingRes, setLoadingRes] = useState(true);
    const [error, setError] = useState(null);

    // Récupérer la réservation au montage
    useEffect(() => {
        const fetchReservation = async () => {
            try {

                setLoadingRes(true);
                setError(null);

                if (!id) {
                    throw new Error('ID de réservation manquant');
                }

                // Utiliser le service directement
                const res = await reservationService.getReservationById(id);

                if (!res) {
                    throw new Error('Réservation non trouvée');
                }

                setReservation(res);
            } catch (err) {
                console.error('[ReservationDetailPage] Erreur:', err);
                setError(err.message);
            } finally {
                setLoadingRes(false);
            }
        };

        fetchReservation();
    }, [id]);

    // Affichage de chargement
    if (loadingRes) {
        return (
            <Layout>
                <div className={styles.container}>
                    <div className={styles.card}>
                        <div className={styles.loadingState}>
                            <FaSpinner className={styles.spinner} />
                            <p>Chargement de la réservation...</p>
                        </div>
                    </div>
                </div>
            </Layout>
        );
    }

    // Affichage d'erreur
    if (error || !reservation) {
        return (
            <>
                <SEO
                    title={`Détail Réservation ${id} | OV Hairstyle`}
                    description="Consultez les détails de votre réservation chez OV Hairstyle."
                    url={`https://ov-hairstyle.vercel.app/reservation/${id}`}
                    image="https://ov-hairstyle.vercel.app/images/og-image.jpeg"
                />
                <Layout>
                    <div className={styles.container}>
                        <div className={styles.card}>
                            <div className={styles.errorState}>
                                <FaExclamationCircle className={styles.errorIcon} />
                                <p>{error || 'Réservation non trouvée'}</p>
                                <Button onClick={() => navigate('/reservation')}>
                                    <FaArrowLeft /> Retour aux réservations
                                </Button>
                            </div>
                        </div>
                    </div>
                </Layout>
            </>
        );
    }

    // Handlers
    const handleSendWhatsApp = async () => {
        try {

            await sendViaWhatsApp(
                reservation,
                userProfile?.name || user.displayName || 'Client',
                userProfile?.phone,
                false
            );
        } catch (error) {
            console.error('Erreur:', error);
        }
    };

    const handleDownloadPDF = async () => {
        try {

            // Attendre que le composant soit visible
            await new Promise(resolve => setTimeout(resolve, 1000));

            await downloadPDF(
                'reservation-pdf-container',
                `Reservation_${reservation.id}.pdf`
            );
        } catch (error) {
            console.error('Erreur:', error);
        }
    };

    const getStatusIcon = (status) => {
        switch (status) {
            case 'confirmée':
                return <FaCheckCircle />;
            case 'annulée':
                return <FaExclamationCircle />;
            case 'pending':
            default:
                return <FaInfoCircle />;
        }
    };

    const getStatusColor = (status) => {
        switch (status) {
            case 'confirmée':
                return '#28a745';
            case 'annulée':
                return '#dc3545';
            case 'pending':
            default:
                return '#ffc107';
        }
    };

    return (
        <Layout>
            <div className={styles.container}>
                {/* Conteneur CACHÉ pour le PDF */}
                <div
                    id="reservation-pdf-container"
                    style={{
                        position: 'fixed',
                        left: '-9999px',
                        top: '-9999px',
                        width: '210mm',
                        height: 'auto',
                        background: 'white',
                        zIndex: -1,
                        overflow: 'visible',
                    }}
                >
                    <ReservationPDF
                        reservation={reservation}
                        clientName={userProfile?.name || user.displayName || 'Client'}
                        clientEmail={user.email}
                    />
                </div>

                {/* Contenu visible */}
                <div className={styles.card}>
                    {/* Header */}
                    <div className={styles.header}>
                        <h1>Détail de la Réservation</h1>
                        <Button
                            variant="ghost"
                            onClick={() => navigate('/reservation')}
                        >
                            <FaArrowLeft /> Retour
                        </Button>
                    </div>

                    {/* Status Badge */}
                    <div className={styles.statusBadge}>
                        <span
                            className={styles.statusIcon}
                            style={{ color: getStatusColor(reservation.status) }}
                        >
                            {getStatusIcon(reservation.status)}
                        </span>
                        <span className={styles.statusText}>
                            {reservation.status === 'confirmée' && 'Confirmée'}
                            {reservation.status === 'annulée' && 'Annulée'}
                            {reservation.status === 'pending' && 'En attente'}
                        </span>
                    </div>

                    {/* Details Grid */}
                    <div className={styles.detailsGrid}>
                        {/* ID */}
                        <div className={styles.detailCard}>
                            <div className={styles.detailIcon} style={{ color: '#d4af37' }}>
                                <FaInfoCircle />
                            </div>
                            <div className={styles.detailContent}>
                                <span className={styles.detailLabel}>ID Réservation</span>
                                <span className={styles.detailValue}>{reservation.id}</span>
                            </div>
                        </div>

                        {/* Service */}
                        <div className={styles.detailCard}>
                            <div className={styles.detailIcon} style={{ color: '#d4af37' }}>
                                <FaCheckCircle />
                            </div>
                            <div className={styles.detailContent}>
                                <span className={styles.detailLabel}>Service</span>
                                <span className={styles.detailValue}>
                                    {reservation.serviceName || reservation.service}
                                </span>
                            </div>
                        </div>

                        {/* Date */}
                        <div className={styles.detailCard}>
                            <div className={styles.detailIcon} style={{ color: '#d4af37' }}>
                                <FaCalendarAlt />
                            </div>
                            <div className={styles.detailContent}>
                                <span className={styles.detailLabel}>Date</span>
                                <span className={styles.detailValue}>
                                    {new Date(reservation.date).toLocaleDateString('fr-FR', {
                                        weekday: 'long',
                                        year: 'numeric',
                                        month: 'long',
                                        day: 'numeric',
                                    })}
                                </span>
                            </div>
                        </div>

                        {/* Time */}
                        <div className={styles.detailCard}>
                            <div className={styles.detailIcon} style={{ color: '#d4af37' }}>
                                <FaClock />
                            </div>
                            <div className={styles.detailContent}>
                                <span className={styles.detailLabel}>Heure</span>
                                <span className={styles.detailValue}>{reservation.time}</span>
                            </div>
                        </div>

                        {/* Duration */}
                        {reservation.duration && (
                            <div className={styles.detailCard}>
                                <div className={styles.detailIcon} style={{ color: '#d4af37' }}>
                                    <FaClock />
                                </div>
                                <div className={styles.detailContent}>
                                    <span className={styles.detailLabel}>Durée</span>
                                    <span className={styles.detailValue}>
                                        {reservation.duration} min
                                    </span>
                                </div>
                            </div>
                        )}

                        {/* Price */}
                        {reservation.price && (
                            <div className={styles.detailCard}>
                                <div className={styles.detailIcon} style={{ color: '#d4af37' }}>
                                    <FaDollarSign />
                                </div>
                                <div className={styles.detailContent}>
                                    <span className={styles.detailLabel}>Prix</span>
                                    <span className={styles.detailValue}>
                                        {reservation.price}€
                                    </span>
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Notes */}
                    {reservation.notes && (
                        <div className={styles.notesSection}>
                            <div className={styles.notesHeader}>
                                <FaFileAlt style={{ marginRight: '8px' }} />
                                Notes
                            </div>
                            <div className={styles.notesContent}>
                                {reservation.notes}
                            </div>
                        </div>
                    )}

                    {/* Actions */}
                    <div className={styles.actions}>
                        <Button
                            variant="primary"
                            onClick={handleDownloadPDF}
                            loading={sharingLoading}
                            disabled={sharingLoading}
                        >
                            <FaFileDownload /> Télécharger PDF
                        </Button>
                        <Button
                            variant="success"
                            onClick={handleSendWhatsApp}
                            loading={sharingLoading}
                            disabled={sharingLoading}
                        >
                            <FaWhatsapp /> Envoyer via WhatsApp
                        </Button>
                    </div>
                </div>
            </div>
        </Layout>
    );
}
